import { useCallback, useMemo } from "react";

import { useShallowEqualSelector } from "../../../common/hooks/useShallowEqualSelector";
import { useAppDispatch } from "../../../store/appDispatch";
import type { StoreState } from "../../../store/storeState";
import {
  getCreateFormFieldsToShow,
  type SpecificFieldId,
} from "../../../use-cases/create/utils/createFormFieldsUtil";
import {
  type CreateFormIdVariant,
  type CreateResult,
  requestSubmit,
} from "../createFormActions";
import { useCreateFormInstanceId } from "../CreateFormProvider";
import type { CreateFormState } from "../createFormReducer";
import {
  type CreateValidationErrors,
  getCreateFormIsSubmitting,
  getCreateFormState,
  getCreateMeta,
  getCreateResult,
} from "../createReducer";

interface UseCreateFormState {
  fieldsToShow: Set<SpecificFieldId>;
  formState: CreateFormState;
  initialized: boolean;
  result: CreateResult | null;
  submit: () => void;
  submitting: boolean;
  validationErrors: CreateValidationErrors;
}

const selectFormState =
  (formId: CreateFormIdVariant) => (state: StoreState) => ({
    formState: getCreateFormState(formId)(state),
    initialized: getCreateMeta(formId)(state).initialized,
    result: getCreateResult(formId)(state),
    submitting: getCreateFormIsSubmitting(formId)(state),
    validationErrors: getCreateMeta(formId)(state).validationErrors,
  });

export const useCreateFormState = (): UseCreateFormState => {
  const formId = useCreateFormInstanceId();

  const { formState, initialized, result, submitting, validationErrors } =
    useShallowEqualSelector(selectFormState(formId));

  const dispatch = useAppDispatch();

  const submit = useCallback(() => {
    dispatch(requestSubmit(formId));
  }, [dispatch, formId]);

  const fieldsToShow = useMemo(
    () => getCreateFormFieldsToShow(formState),
    [formState],
  );

  return {
    fieldsToShow,
    formState,
    initialized,
    result,
    submit,
    submitting,
    validationErrors,
  };
};
